import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { HERO_STATEMENTS } from "../data/content";
import { EASE } from "../components/Shared";

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [booted, setBooted] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setBooted(true), 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_STATEMENTS.length), 4200);
    return () => clearInterval(id);
  }, []);

  const lines = HERO_STATEMENTS[index];

  return (
    <section ref={ref} className="always-dark relative h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src="/images/stock/g-hero.jpg"
          alt="Rutu Bhonsle couture — dramatic black editorial silhouette"
          className="h-[115%] w-full object-cover object-top"
          initial={{ scale: 1.15, filter: "brightness(0.6)" }}
          animate={{ scale: 1, filter: "brightness(0.75)" }}
          transition={{ duration: 2.4, ease: EASE }}
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-ink/40" />

      <motion.div
        className="absolute right-6 top-1/2 hidden -translate-y-1/2 rotate-90 items-center gap-4 md:flex"
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        <span className="h-px w-16 bg-flame" />
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-bone/60">Est. Nagpur — Worn Worldwide</span>
      </motion.div>

      <motion.div className="relative z-10 flex h-full flex-col justify-end px-5 pb-24 sm:px-10 md:pb-16" style={{ y: textY, opacity: fade }}>
        <motion.p
          className="mb-8 max-w-[300px] font-mono text-[10px] uppercase leading-loose tracking-[0.35em] text-flame sm:mb-6 sm:max-w-none sm:text-[11px] sm:tracking-[0.4em]"
          initial={{ opacity: 0, y: 20 }}
          animate={booted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          data-testid="hero-eyebrow"
        >
          Rutu Bhonsle — House of Contemporary Indian Fashion
        </motion.p>

        <div className="min-h-[38vw] sm:min-h-[30vw] lg:min-h-[22vw]">
          <AnimatePresence mode="wait">
            <motion.h1
              key={index}
              className="font-serif text-[13.5vw] font-light leading-[0.95] tracking-tight sm:text-[11vw] lg:text-[8.5vw]"
              data-testid="hero-statement"
              exit={{ opacity: 0, y: -30, transition: { duration: 0.45, ease: EASE } }}
            >
              {lines.map((l, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    className={`block ${i === 1 ? "text-flame sm:pl-[6vw]" : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1.1, delay: 0.5 + i * 0.14, ease: EASE }}
                  >
                    {l}
                  </motion.span>
                </span>
              ))}
            </motion.h1>
          </AnimatePresence>
        </div>

        <motion.div
          className="mt-10 flex flex-wrap items-end justify-between gap-8"
          initial={{ opacity: 0, y: 30 }}
          animate={booted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1.4, ease: EASE }}
        >
          <div className="flex items-center gap-3">
            {HERO_STATEMENTS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Statement ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => setIndex(i)}
                className={`h-1 transition-all duration-500 ${i === index ? "w-10 bg-flame" : "w-4 bg-bone/30 hover:bg-bone/60"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4" data-testid="hero-scroll-prompt">
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-bone/70">Scroll to enter</span>
            <span className="relative flex h-10 w-6 items-start justify-center border border-bone/30 pt-1.5">
              <span className="h-2 w-px animate-scroll-dot bg-flame" />
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
